import {useCallback} from 'react';
import {useMutation, useQuery} from 'react-query';

import {
    getOrgRoles,
    getOrgRole,
    addOrgRole,
    updateOrgRole,
    deleteOrgRole,
    addOrgRoleForUser,
    removeOrgRoleForUser
} from 'client/common/utils/api-client';
import {queryClient} from 'client/common/utils/react-query';
import {getOrgRoleKey, getOrgRolesKey} from '.';

export function useOrgRoles(orgId: number) {
    return useQuery(getOrgRolesKey(orgId), async () => {
        const {data} = await getOrgRoles({orgId});

        return data ?? [];
    });
}

export function useOrgRole(orgId: number, roleId: number) {
    return useQuery(getOrgRoleKey(orgId, roleId), async () => {
        const {data} = await getOrgRole({orgId, roleId});

        return data ?? [];
    });
}

export function useOrgAddRole(orgId: number) {
    const key = getOrgRolesKey(orgId);

    const {mutateAsync} = useMutation(addOrgRole, {
        onMutate: async () => {
            await queryClient.cancelQueries(key);
        },
        onSettled: () => {
            queryClient.refetchQueries(key);
        }
    });

    return async (name: string, permissions: string[]) => {
        await mutateAsync({orgId, name, permissions});
    };
}

export function useOrgUpdateRole(orgId: number, roleId: number) {
    const key = getOrgRolesKey(orgId);

    const {mutateAsync} = useMutation(updateOrgRole, {
        onMutate: async ({name, permissions}) => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: {roles: any}) => {
                return {
                    ...old,
                    roles: old.roles.map((role: any) => {
                        if (role.id === roleId) {
                            role.name = name;
                            role.permissions = permissions;
                        }

                        return role;
                    })
                };
            });

            return shapshot;
        },
        onError: (_err, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        },
        onSettled: () => {
            queryClient.refetchQueries(key);
        }
    });

    return useCallback(
        async ({name, permissions}: {name: string; permissions: string[]}) => {
            await mutateAsync({orgId, roleId, name, permissions});
        },
        [mutateAsync, orgId, roleId]
    );
}

export function useOrgDeleteRole(orgId: number, roleId: number) {
    const key = getOrgRolesKey(orgId);

    const {mutateAsync} = useMutation(deleteOrgRole, {
        onMutate: async () => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            queryClient.setQueryData(key, (old: {roles: any}) => {
                return {
                    ...old,
                    roles: old.roles.filter((role: any) => {
                        return role.id !== roleId;
                    })
                };
            });

            return shapshot;
        },
        onError: (_err, _, shapshot) => {
            queryClient.setQueryData(key, shapshot);
        },
        onSettled: () => {
            queryClient.refetchQueries(key);
        }
    });

    return useCallback(async () => {
        await mutateAsync({orgId, roleId});
    }, [mutateAsync, orgId, roleId]);
}

export function useOrgAddRoleForUser(orgId: number, roleId: number) {
    const key = getOrgRoleKey(orgId, roleId);

    const {mutateAsync} = useMutation(addOrgRoleForUser, {
        onMutate: async () => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            return shapshot;
        },
        onSettled: () => {
            queryClient.refetchQueries(key);
        }
    });

    return useCallback(
        async ({userid}) => {
            await mutateAsync({orgId, roleId, userid});
        },
        [mutateAsync, orgId, roleId]
    );
}

export function useOrgRemoveRoleForUser(orgId: number, roleId: number) {
    const key = getOrgRoleKey(orgId, roleId);

    const {mutateAsync} = useMutation(removeOrgRoleForUser, {
        onMutate: async () => {
            await queryClient.cancelQueries(key);

            const shapshot = queryClient.getQueryData(key);

            return shapshot;
        },
        onSettled: () => {
            queryClient.refetchQueries(key);
        }
    });

    return useCallback(
        async ({userid}) => {
            await mutateAsync({orgId, roleId, userid});
        },
        [mutateAsync, orgId, roleId]
    );
}
